import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, ShoppingBag } from "lucide-react";
import { fmtINR } from "../lib/api";
import { useShop } from "../lib/shop";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export const ProductCard = ({ product }) => {
  const { addToCart, toggleWishlist, isWishlisted } = useShop();
  const { user } = useAuth();
  const navigate = useNavigate();
  const wishlisted = isWishlisted(product.id);
  const discount = product.mrp > product.price ? Math.round(((product.mrp - product.price) / product.mrp) * 100) : 0;

  const add = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { navigate("/login"); return; }
    try { await addToCart(product.id, 1); toast.success("Added to bag"); }
    catch { toast.error("Could not add to bag"); }
  };
  const wish = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (!user) { navigate("/login"); return; }
    try { const inW = await toggleWishlist(product.id); toast.success(inW ? "Saved to wishlist" : "Removed"); }
    catch { toast.error("Could not update wishlist"); }
  };

  return (
    <Link
      to={`/product/${product.slug || product.id}`}
      data-testid={`product-card-${product.id}`}
      className="jp-card group block relative overflow-hidden"
    >
      <div className="relative aspect-[4/5] w-full overflow-hidden bg-brand-blush">
        {product.images?.[0] ? (
          <img
            src={product.images[0]}
            alt={product.name}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-brand-muted text-sm">No image</div>
        )}
        {discount > 0 && (
          <span className="absolute left-3 top-3 jp-badge bg-brand-primary text-white">-{discount}%</span>
        )}
        {product.is_featured && (
          <span className="absolute right-3 top-3 jp-badge">Featured</span>
        )}
        <button
          onClick={wish}
          aria-label="Wishlist"
          data-testid={`wishlist-toggle-${product.id}`}
          className={`absolute right-3 bottom-3 h-9 w-9 rounded-full flex items-center justify-center bg-white/90 backdrop-blur border border-brand-line transition-colors ${wishlisted ? "text-brand-primary" : "text-brand-ink hover:text-brand-primary"}`}
        >
          <Heart size={16} fill={wishlisted ? "currentColor" : "none"} />
        </button>
      </div>
      <div className="p-4 flex flex-col gap-2">
        <p className="text-[11px] uppercase tracking-[0.18em] text-brand-muted">{product.category_name}</p>
        <h3 className="font-medium text-brand-ink line-clamp-2 min-h-[2.7rem] leading-snug">{product.name}</h3>
        <div className="flex items-baseline gap-2 mt-auto">
          <span className="text-lg font-semibold text-brand-ink">{fmtINR(product.price)}</span>
          {product.mrp > product.price && (
            <span className="text-sm text-brand-muted line-through">{fmtINR(product.mrp)}</span>
          )}
        </div>
        <button
          onClick={add}
          data-testid={`add-to-cart-${product.id}`}
          className="mt-1 inline-flex items-center justify-center gap-2 border border-brand-primary text-brand-primary text-xs uppercase tracking-[0.18em] py-2 hover:bg-brand-primary hover:text-white transition-colors"
        >
          <ShoppingBag size={14} /> Add to Bag
        </button>
      </div>
    </Link>
  );
};
